import type { ProjectAnnotation, ProjectAnnotationTargetType } from '@/types/project-annotations';
import type { TrackType } from '@/types/storyboard-board';
import {
  getPreviewAnnotationTargetKey,
  type PreviewAnnotationTarget,
} from '@/lib/annotations/preview-annotation-target';
import { resolveAnnotationTargetCategory } from '@/lib/annotations/annotation-filters';
import {
  getAnnotationStoryboardId,
  getStoryboardVideoAnnotationIdentity,
  isSamePreviewAnnotationTarget,
} from '@/lib/annotations/storyboard-annotation-unread-counts';

const PARSE_FLOW_ANNOTATION_CONTEXT = 2;

export function isAnnotationActionable(
  annotation: Pick<ProjectAnnotation, 'authorUserId' | 'status'>,
  currentUserId: string | null | undefined
): boolean {
  if (!currentUserId) return false;
  if (annotation.status === 3 || annotation.status === 4) return false;
  if (annotation.authorUserId === currentUserId) {
    return annotation.status === 2;
  }
  return annotation.status === 1;
}

export function isAnnotationAttentionWorthy(
  annotation: Pick<ProjectAnnotation, 'authorUserId' | 'status' | 'hasUnreadReply'>,
  currentUserId: string | null | undefined
): boolean {
  return (
    isAnnotationActionable(annotation, currentUserId) ||
    Boolean(currentUserId && annotation.hasUnreadReply)
  );
}

export function countActionableAnnotations(
  annotations: readonly ProjectAnnotation[],
  currentUserId: string | null | undefined
): number {
  if (!currentUserId) return 0;
  return annotations.filter((annotation) => isAnnotationAttentionWorthy(annotation, currentUserId))
    .length;
}

export function buildActionableStoryboardCounts(
  annotations: readonly ProjectAnnotation[],
  currentUserId: string | null | undefined
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  if (!currentUserId) return counts;

  for (const annotation of annotations) {
    if (!isAnnotationAttentionWorthy(annotation, currentUserId)) continue;
    if (resolveAnnotationTargetCategory(annotation) !== 'storyboard') continue;
    const storyboardId = getAnnotationStoryboardId(annotation);
    if (!storyboardId) continue;
    counts.set(storyboardId, (counts.get(storyboardId) ?? 0) + 1);
  }

  return counts;
}

export function resolveResourceLibraryResourceId(
  annotation: Pick<ProjectAnnotation, 'targetType' | 'targetId' | 'metadata'>
): string | null {
  if (
    (annotation.targetType === 'resource_element' ||
      annotation.targetType === 'project_resource') &&
    annotation.targetId
  ) {
    return annotation.targetId;
  }
  if (annotation.targetType === 'resource_tag') {
    return annotation.metadata.annotationTarget?.resourceId ?? null;
  }
  return null;
}

export type StoryboardBoardAnnotationTrackType = Extract<
  TrackType,
  'video' | 'dialogue' | 'voice' | 'sound'
>;

const ANNOTATION_TARGET_TO_BOARD_TRACK: Partial<
  Record<ProjectAnnotationTargetType, StoryboardBoardAnnotationTrackType>
> = {
  storyboard_video: 'video',
  dialogue: 'dialogue',
  voiceover: 'voice',
  sound_effect: 'sound',
};

export function buildStoryboardTrackActionableCountKey(
  trackType: StoryboardBoardAnnotationTrackType,
  storyboardId: string
): string {
  return `${trackType}:${storyboardId}`;
}

export function getStoryboardTrackActionableCount(
  counts: ReadonlyMap<string, number> | undefined,
  trackType: StoryboardBoardAnnotationTrackType,
  storyboardId: string
): number {
  return counts?.get(buildStoryboardTrackActionableCountKey(trackType, storyboardId)) ?? 0;
}

/** 分镜看板：按轨道类型 + 分镜 ID 聚合待办（对白 / 配音 / 音效 / 视频轨道块） */
export function buildActionableStoryboardTrackCounts(
  annotations: readonly ProjectAnnotation[],
  currentUserId: string | null | undefined
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  if (!currentUserId) return counts;

  for (const annotation of annotations) {
    if (!isAnnotationAttentionWorthy(annotation, currentUserId)) continue;
    const targetKind = annotation.metadata.annotationTarget?.kind ?? annotation.targetType;
    const boardTrackType = ANNOTATION_TARGET_TO_BOARD_TRACK[targetKind];
    if (!boardTrackType) continue;
    const storyboardId = getAnnotationStoryboardId(annotation);
    if (!storyboardId) continue;
    const key = buildStoryboardTrackActionableCountKey(boardTrackType, storyboardId);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return counts;
}

/** 分镜看板：统计分镜块下全部子目标待办（按 targetStoryboardId 聚合） */
export function buildActionableStoryboardShotCounts(
  annotations: readonly ProjectAnnotation[],
  currentUserId: string | null | undefined
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  if (!currentUserId) return counts;

  for (const annotation of annotations) {
    if (!isAnnotationAttentionWorthy(annotation, currentUserId)) continue;
    const isParseFlowStoryboardAnnotation =
      annotation.context === PARSE_FLOW_ANNOTATION_CONTEXT &&
      resolveAnnotationTargetCategory(annotation) === 'storyboard';
    if (isParseFlowStoryboardAnnotation) continue;
    const storyboardId = getAnnotationStoryboardId(annotation);
    if (!storyboardId) continue;
    counts.set(storyboardId, (counts.get(storyboardId) ?? 0) + 1);
  }

  return counts;
}

/** 资源库：按资产 ID 聚合待办（元素 / 标签 / 素材） */
export function buildActionableResourceLibraryCounts(
  annotations: readonly ProjectAnnotation[],
  currentUserId: string | null | undefined
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  if (!currentUserId) return counts;

  for (const annotation of annotations) {
    if (!isAnnotationAttentionWorthy(annotation, currentUserId)) continue;
    const resourceId = resolveResourceLibraryResourceId(annotation);
    if (!resourceId) continue;
    counts.set(resourceId, (counts.get(resourceId) ?? 0) + 1);
  }

  return counts;
}

export function buildActionableTargetCounts(
  annotations: readonly ProjectAnnotation[],
  currentUserId: string | null | undefined
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  if (!currentUserId) return counts;

  for (const annotation of annotations) {
    if (!isAnnotationAttentionWorthy(annotation, currentUserId)) continue;
    if (!annotation.targetType) continue;
    const videoId =
      annotation.targetType === 'storyboard_video'
        ? getStoryboardVideoAnnotationIdentity(annotation)
        : null;
    const targetId = annotation.targetType === 'storyboard_video' ? videoId : annotation.targetId;
    if (!targetId) continue;
    const key = `${annotation.targetType}:${targetId}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return counts;
}

export function getActionableTargetCount(
  counts: ReadonlyMap<string, number> | undefined,
  target: PreviewAnnotationTarget | null | undefined
): number {
  const key = getPreviewAnnotationTargetKey(target);
  return key ? (counts?.get(key) ?? 0) : 0;
}

export function getPreviewAnnotationTargetActionableCount(
  target: PreviewAnnotationTarget | null | undefined,
  annotations: readonly ProjectAnnotation[],
  currentUserId: string | null | undefined
): number {
  if (!target || !currentUserId) return 0;
  return annotations.filter(
    (annotation) =>
      isAnnotationAttentionWorthy(annotation, currentUserId) &&
      isSamePreviewAnnotationTarget(annotation, target)
  ).length;
}
